import { gql } from "graphql-request";
import { z } from "zod";

import { Image, ImageFragment } from "../image";
import { Sys, SysFragment } from "../sys";
import {
  TextBlockSchema,
  TextBlockFragment,
  ImageBlockSchema,
  ImageBlockFragment,
  FeatureBlockSchema,
  FeatureBlockFragment,
  VideoBlockSchema,
  VideoBlockFragment,
  PollBlockSchema,
  PollBlockFragment,
  LeaderboardBlockFragment,
  LeaderboardBlockSchema,
} from "./blocks";
import { ArticleVisibility, ShowInNewsFeedKind } from "../../../types";
import { UnifiedButton, UnifiedButtonFragment } from "../unifiedButton";
import {
  CarouselEventProgressTrackerEntryFragment,
  CarouselEventProgressTrackerEntrySchema,
} from "./carouselEventProgressTrackerEntry";

export const NewsEntryArticleSchema = z.object({
  __typename: z.literal("NewsEntryArticle"),
  sys: Sys,
  title: z.string().nullable(),
  blogSlug: z.string().nullish(),
  heroImage: Image.nullable(),
  heroImageEn: Image.nullable(),
  heroProgressTracker:
    CarouselEventProgressTrackerEntrySchema.nullable().optional(),
  largeThumbnail: Image.nullable(),
  largeThumbnailEn: Image.nullable(),
  websiteHeroImage: Image.nullable().optional(),
  websiteHeroImageEn: Image.nullable().optional(),
  whereToPublish: ArticleVisibility.nullable().optional(),
  showInNewsFeed: ShowInNewsFeedKind.nullish(),
  bodyCollection: z
    .object({
      items: z.array(
        z.discriminatedUnion("__typename", [
          TextBlockSchema,
          ImageBlockSchema,
          FeatureBlockSchema,
          VideoBlockSchema,
          UnifiedButton,
          PollBlockSchema,
          LeaderboardBlockSchema,
        ]),
      ),
    })
    .nullish(),
});

export const NewsEntryArticle = NewsEntryArticleSchema;
export type NewsEntryArticle = z.infer<typeof NewsEntryArticleSchema>;

export const NewsEntryArticleFragment = gql`
  fragment NewsEntryArticleFragment on NewsEntryArticle {
    sys {
      ...SysFragment
    }
    title
    blogSlug
    whereToPublish
    showInNewsFeed
    heroImage {
      ...ImageFragment
    }
    heroImageEn: heroImage(locale: "en") {
      ...ImageFragment
    }

    heroProgressTracker {
      ...CarouselEventProgressTrackerEntryFragment
    }

    websiteHeroImage {
      ...ImageFragment
    }

    websiteHeroImageEn: websiteHeroImage(locale: "en") {
      ...ImageFragment
    }

    largeThumbnail {
      ...ImageFragment
    }
    largeThumbnailEn: largeThumbnail(locale: "en") {
      ...ImageFragment
    }

    bodyCollection(limit: 199) {
      items {
        __typename
        ... on FeatureBlock {
          ...FeatureBlockFragment
        }
        ... on ImageBlock {
          ...ImageBlockFragment
        }
        ... on TextBlock {
          ...TextBlockFragment
        }
        ... on VideoBlock {
          ...VideoBlockFragment
        }
        ... on UnifiedButton {
          ...UnifiedButtonFragment
        }
        ... on PollBlock {
          ...PollBlockFragment
        }
        ... on LeaderboardBlock {
          ...LeaderboardBlockFragment
        }
      }
    }
  }

  ${ImageFragment}
  ${SysFragment}
  ${TextBlockFragment}
  ${ImageBlockFragment}
  ${FeatureBlockFragment}
  ${VideoBlockFragment}
  ${UnifiedButtonFragment}
  ${PollBlockFragment}
  ${LeaderboardBlockFragment}
  ${CarouselEventProgressTrackerEntryFragment}
`;
